<template>
  <Context ref="context" overflow-scroll max-height-if-outside-viewport>
    <div class="context__menu-title">{{ view.name }} ({{ view.id }})</div>
    <ul class="context__menu">
      <component
        :is="component"
        v-for="(component, i) in additionalContextComponents"
        :key="i"
        :workspace="database.workspace"
        :database="database"
        :table="table"
        :view="view"
        @hide-context="$refs.context.hide()"
      />
      <li
        v-if="
          hasValidExporter &&
          $hasPermission(
            'database.table.run_export',
            table,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <button
          type="button"
          class="context__menu-item-link"
          @click="exportView()"
        >
          <i class="context__menu-item-icon iconoir-share-ios"></i>
          {{ $t('viewContext.exportView') }}
        </button>
      </li>
      <li
        v-if="
          $hasPermission(
            'database.table.import_rows',
            table,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <button
          type="button"
          class="context__menu-item-link"
          @click="importFile()"
        >
          <i class="context__menu-item-icon iconoir-import"></i>
          {{ $t('viewContext.importFile') }}
        </button>
      </li>
      <li
        v-if="
          $hasPermission(
            'database.table.view.duplicate',
            view,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <button
          type="button"
          class="context__menu-item-link"
          :class="{ 'context__menu-item-link--loading': duplicateLoading }"
          @click="duplicateView()"
        >
          <i class="context__menu-item-icon iconoir-copy"></i>
          {{ $t('viewContext.duplicateView') }}
        </button>
      </li>
      <li
        v-if="
          canCopyConfiguration &&
          $hasPermission(
            'database.table.view.update',
            view,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <a
          class="context__menu-item-link"
          @click="openCopyConfigurationModal()"
        >
          <i class="context__menu-item-icon iconoir-multiple-pages"></i>
          {{ $t('viewContext.copyConfiguration') }}
        </a>
      </li>
      <li
        v-for="(
          changeViewOwnershipTypeComponent, index
        ) in changeViewOwnershipTypeMenuItems"
        :key="'view-ownership-type-' + index"
      >
        <component
          :is="changeViewOwnershipTypeComponent"
          :view="view"
          :database="database"
        ></component>
      </li>
      <li
        v-if="
          $hasPermission(
            'database.table.create_webhook',
            table,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <button
          type="button"
          class="context__menu-item-link"
          @click="openWebhookModal()"
        >
          <i class="context__menu-item-icon iconoir-globe"></i>
          {{ $t('viewContext.webhooks') }}
        </button>
      </li>
      <li
        v-if="
          canSetDefaultValues &&
          $hasPermission(
            'database.table.view.update_default_values',
            view,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <button
          type="button"
          class="context__menu-item-link"
          @click="openDefaultValuesModal()"
        >
          <i class="context__menu-item-icon iconoir-settings"></i>
          {{ $t('viewContext.defaultRowValues') }}
          <Badge
            v-if="enabledDefaultRowValues.length >= 1"
            color="neutral"
            size="small"
            >{{ enabledDefaultRowValues.length }}</Badge
          >
        </button>
      </li>
      <li
        v-if="
          $hasPermission(
            'database.table.view.update',
            view,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <button
          type="button"
          class="context__menu-item-link"
          @click="enableRename()"
        >
          <i class="context__menu-item-icon iconoir-edit-pencil"></i>
          {{ $t('viewContext.renameView') }}
        </button>
      </li>
      <li
        v-if="
          $hasPermission(
            'database.table.view.update',
            view,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <button
          ref="customiseLink"
          type="button"
          class="context__menu-item-link"
          @click="openCustomise()"
        >
          <i class="context__menu-item-icon iconoir-color-picker"></i>
          {{ $t('viewContext.customiseView') }}
        </button>
      </li>
      <!-- Moving a view between folders is a menu item rather than only a drag:
           the destination list can be longer than the rail shows, and a drag
           has no discoverable affordance on a collapsed rail or a touch
           screen. Dragging is offered too, in the rail itself. -->
      <li
        v-if="
          folders.length > 0 &&
          $hasPermission(
            'database.table.view.update',
            view,
            database.workspace.id
          )
        "
        class="context__menu-item"
      >
        <button
          ref="moveToFolderLink"
          type="button"
          class="context__menu-item-link"
          @click="openMoveToFolder()"
        >
          <i class="context__menu-item-icon iconoir-folder"></i>
          {{ $t('viewContext.moveToFolder') }}
        </button>
        <ViewMoveToFolderContext
          ref="moveToFolderContext"
          :view="view"
          :folders="folders"
          @moved="$refs.context.hide()"
        ></ViewMoveToFolderContext>
      </li>
      <li
        v-if="
          $hasPermission(
            'database.table.view.delete',
            view,
            database.workspace.id
          )
        "
        class="context__menu-item context-menu-item--with-separator"
      >
        <button
          type="button"
          class="context__menu-item-link context__menu-item-link--delete"
          :class="{ 'context__menu-item-link--loading': deleteLoading }"
          @click="deleteView()"
        >
          <i class="context__menu-item-icon iconoir-bin"></i>
          {{ $t('viewContext.deleteView') }}
        </button>
      </li>
    </ul>
    <ExportTableModal
      ref="exportViewModal"
      :database="database"
      :table="table"
      :view="view"
    />
    <ImportFileModal
      ref="importFileModal"
      :database="database"
      :table="table"
      :fields="fields"
    />
    <ViewIdentityContext ref="customiseContext" :view="view" />
    <WebhookModal ref="webhookModal" :database="database" :table="table" />
    <DefaultValuesModal
      ref="defaultValuesModal"
      :view="view"
      :table="table"
      :database="database"
      :store-prefix="storePrefix"
    />
    <CopyViewConfigurationModal
      ref="copyViewConfigurationModal"
      :view="view"
      :database="database"
    />
  </Context>
</template>

<script>
import { mapGetters } from 'vuex'
import context from '@baserow/modules/core/mixins/context'
import viewTypeHasExporterTypes from '@baserow/modules/database/utils/viewTypeHasExporterTypes'
import ImportFileModal from '@baserow/modules/database/components/table/ImportFileModal'
import { notifyIf } from '@baserow/modules/core/utils/error'
import ExportTableModal from '@baserow/modules/database/components/export/ExportTableModal'
import WebhookModal from '@baserow/modules/database/components/webhook/WebhookModal.vue'
import DefaultValuesModal from '@baserow/modules/database/components/view/DefaultValuesModal.vue'
import ViewIdentityContext from '@baserow/modules/database/components/view/ViewIdentityContext.vue'
import ViewMoveToFolderContext from '@baserow/modules/database/components/view/ViewMoveToFolderContext.vue'
import CopyViewConfigurationModal from '@baserow/modules/database/components/view/CopyViewConfigurationModal.vue'
import { getCompatibleSourceViews } from '@baserow/modules/database/utils/copyViewConfiguration'

export default {
  name: 'ViewContext',
  components: {
    ExportTableModal,
    WebhookModal,
    ImportFileModal,
    DefaultValuesModal,
    ViewIdentityContext,
    ViewMoveToFolderContext,
    CopyViewConfigurationModal,
  },
  mixins: [context],
  props: {
    database: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    storePrefix: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['enable-rename'],
  data() {
    return {
      duplicateLoading: false,
      deleteLoading: false,
    }
  },
  computed: {
    hasValidExporter() {
      return viewTypeHasExporterTypes(this.view.type, this.$registry)
    },
    canSetDefaultValues() {
      const viewType = this.$registry.get('view', this.view.type)
      return viewType.canSetDefaultValues
    },
    ...mapGetters({
      fields: 'field/getAll',
      allViews: 'view/getAll',
    }),
    canCopyConfiguration() {
      // Without a compatible source view, a form view or a table with a
      // single view for example, the copy configuration modal would be a dead
      // end.
      return (
        getCompatibleSourceViews(
          this.$registry,
          this.allViews,
          this.view,
          this.database.workspace.id
        ).length > 0
      )
    },
    changeViewOwnershipTypeMenuItems() {
      const activeOwnershipTypes = Object.values(
        this.$registry.getAll('viewOwnershipType')
      )

      return activeOwnershipTypes
        .map((viewOwnershipType) => {
          return viewOwnershipType.getChangeOwnershipTypeMenuItemComponent()
        })
        .filter((component) => component !== null)
    },
    additionalContextComponents() {
      return Object.values(this.$registry.getAll('plugin'))
        .reduce(
          (components, plugin) =>
            components.concat(
              plugin.getAdditionalViewContextComponents(
                this.database.workspace,
                this.table,
                this.view
              )
            ),
          []
        )
        .filter((component) => component !== null)
    },
    enabledDefaultRowValues() {
      return this.view.default_row_values.filter(
        (defaultRowValue) => defaultRowValue.enabled
      )
    },
    /**
     * Only the folders a view of this ownership type can actually be moved
     * into. The backend rejects a mismatch with
     * `ERROR_VIEW_FOLDER_OWNERSHIP_TYPE_MISMATCH`, so offering the others would
     * be offering a guaranteed error.
     */
    folders() {
      return this.$store.getters['view/getAllFoldersOrdered'].filter(
        (folder) => folder.ownership_type === this.view.ownership_type
      )
    },
  },
  methods: {
    enableRename() {
      this.$refs.context.hide()
      this.$emit('enable-rename')
    },
    async deleteView() {
      this.deleteLoading = true

      try {
        await this.$store.dispatch('view/delete', this.view)
      } catch (error) {
        notifyIf(error, 'view')
      }

      this.deleteLoading = false
    },
    async duplicateView() {
      this.duplicateLoading = true
      let newView

      try {
        newView = await this.$store.dispatch('view/duplicate', this.view)
      } catch (error) {
        notifyIf(error, 'view')
      }

      this.$refs.context.hide()
      this.duplicateLoading = false

      // Redirect to the newly created view.
      this.$router.push({
        name: 'database-table',
        params: {
          databaseId: this.table.database_id,
          tableId: this.table.id,
          viewId: newView.id,
        },
      })
    },
    exportView() {
      this.$refs.context.hide()
      this.$refs.exportViewModal.show()
    },
    importFile() {
      this.$refs.context.hide()
      this.$refs.importFileModal.show()
    },
    openWebhookModal() {
      this.$refs.context.hide()
      this.$refs.webhookModal.show()
    },
    openDefaultValuesModal() {
      this.$refs.context.hide()
      this.$refs.defaultValuesModal.show()
    },
    /**
     * The picker opens beside the menu rather than replacing it, because every
     * click in it writes immediately and the user usually makes two choices --
     * an icon and a colour -- before they are done.
     */
    openCustomise() {
      this.$refs.customiseContext.toggle(
        this.$refs.customiseLink,
        'left',
        'top',
        0,
        4
      )
    },
    openMoveToFolder() {
      this.$refs.moveToFolderContext.toggle(
        this.$refs.moveToFolderLink,
        'left',
        'top',
        0,
        4
      )
    },
    openCopyConfigurationModal() {
      this.$refs.context.hide()
      this.$refs.copyViewConfigurationModal.show()
    },
  },
}
</script>
